import { formatCalendarDate } from "@/lib/date";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import { Edit } from "lucide-react";
import OrganismForm from "@/features/inventory/OrganismForm";

export const Route = createFileRoute("/inventory/organisms")({
  component: OrganismsPage,
});

type Organism = RouterOutputs["inventory"]["organism"]["list"][number];

function OrganismsPage() {
  const { data: organisms = [] } = trpc.inventory.organism.list.useQuery({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingOrganism, setEditingOrganism] = useState<Organism | null>(null);

  const columns = useMemo(() => {
    const cols: DataTableColumnDef<Organism, any>[] = [
      {
        id: "actions",
        header: "",
        size: 50,
        enableSorting: false,
        enableResizing: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setEditingOrganism(row.original)}
            title="Edit Organism"
          >
            <Edit className="h-4 w-4" />
          </Button>
        ),
      },
      {
        id: "nickname",
        header: "Nickname",
        accessorKey: "nickname",
        cellTypeId: "string",
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        cellTypeId: "string",
      },
      {
        id: "stock_type",
        header: "Stock Type",
        accessorKey: "stock_type",
        cellTypeId: "string",
      },
      {
        id: "storage_location",
        header: "Location",
        accessorKey: "storage_location",
        cell: ({ row }) => row.original.storage_location ?? "-",
      },
      {
        id: "stock_left",
        header: "Stock Left",
        accessorKey: "stock_left",
        cell: ({ row }) => row.original.stock_left ?? "-",
      },
      {
        id: "collection_no",
        header: "Collection #",
        accessorKey: "collection_no",
        cell: ({ row }) => row.original.collection_no ?? "-",
      },
      {
        id: "source_organism",
        header: "Source Stock",
        accessorKey: "source_organism_id",
        cell: ({ row }) => {
          const sourceId = row.original.source_organism_id;
          if (!sourceId) return "-";
          const source = organisms.find((o) => o.id === sourceId);
          return source ? source.nickname : "-";
        },
      },
      {
        id: "stock_creation_date",
        header: "Created",
        accessorKey: "stock_creation_date",
        cell: ({ row }) =>
          row.original.stock_creation_date
            ? formatCalendarDate(row.original.stock_creation_date)
            : "-",
      },
    ];
    return cols;
  }, [organisms]);

  return (
    <div className="gap-4 flex flex-col grow min-h-0 h-full">
      <DataTable
        data={organisms}
        columns={columns}
        searchPlaceholder="Search organisms..."
        leftCustomToolbar={
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">Create Organism</Button>
            </DialogTrigger>
            <DialogContent>
              <OrganismForm
                onComplete={() => setIsCreateOpen(false)}
                organisms={organisms}
              />
            </DialogContent>
          </Dialog>
        }
        enableColumnResizing
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />

      <Dialog
        open={!!editingOrganism}
        onOpenChange={(open) => {
          if (!open) setEditingOrganism(null);
        }}
      >
        <DialogContent>
          {editingOrganism && (
            <OrganismForm
              organism={editingOrganism}
              onComplete={() => setEditingOrganism(null)}
              organisms={organisms}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
